(() => {
  "use strict";
  const base = "https://caqaresources.com.au",
    cache = new Map(),
    money = (c) =>
      new Intl.NumberFormat("en-AU", {
        style: "currency",
        currency: "AUD",
      }).format(c / 100);
  const el = (tag, cls, text) => {
    const e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text !== undefined) e.textContent = text;
    return e;
  };
  async function request(url) {
    const r = await fetch(url, {
      credentials: "omit",
      signal: AbortSignal.timeout(20000),
    });
    if (!r.ok)
      throw Error(
        r.status === 429
          ? "The resource catalogue is busy. Please try again shortly."
          : "Product information is temporarily unavailable.",
      );
    return r;
  }
  let productActive = 0;
  const productWaiters = [];
  async function requestProduct(url) {
    if (productActive < 3) productActive++;
    else await new Promise(resolve => productWaiters.push(resolve));
    try { return await (await request(url)).json(); }
    finally { if (productWaiters.length) productWaiters.shift()(); else productActive--; }
  }
  const product = (handle) => {
    if (!/^[a-z0-9-]+$/.test(handle))
      return Promise.reject(Error("Invalid product"));
    if (!cache.has(handle))
      cache.set(
        handle,
        requestProduct(base + "/products/" + handle + ".js")
          .catch((e) => {
            cache.delete(handle);
            throw e;
          }),
      );
    return cache.get(handle);
  };
  const imageURL = (url) => {
    if (!url || typeof url !== "string") return "";
    try {
      const u = new URL(url, base);
      if (
        u.protocol !== "https:" ||
        !(
          u.hostname === "cdn.shopify.com" ||
          u.hostname === "caqaresources.com.au" ||
          u.hostname.endsWith(".shopify.com")
        )
      )
        return "";
      u.searchParams.set("width", "650");
      return u.href;
    } catch {
      return "";
    }
  };
  function safeDescription(html) {
    const parsed = new DOMParser().parseFromString(html || "", "text/html");
    const allow = new Set([
      "P",
      "DIV",
      "H2",
      "H3",
      "H4",
      "H5",
      "UL",
      "OL",
      "LI",
      "STRONG",
      "B",
      "EM",
      "I",
      "BR",
      "A",
      "TABLE",
      "THEAD",
      "TBODY",
      "TR",
      "TD",
      "TH",
      "SPAN",
    ]);
    parsed
      .querySelectorAll(
        "script,style,iframe,form,input,button,object,embed,link,meta,img,video,audio",
      )
      .forEach((n) => n.remove());
    for (const node of [...parsed.body.querySelectorAll("*")].reverse()) {
      if (!allow.has(node.tagName)) {
        node.replaceWith(...node.childNodes);
        continue;
      }
      const href = node.getAttribute("href");
      for (const a of [...node.attributes]) node.removeAttribute(a.name);
      if (node.tagName === "A" && href) {
        try {
          const u = new URL(href, base);
          if (["https:", "mailto:", "tel:"].includes(u.protocol)) {
            node.href = u.href;
            node.target = "_blank";
            node.rel = "noopener";
          }
        } catch {}
      }
      if (node.tagName === "H2")
        node.replaceWith(
          Object.assign(document.createElement("h3"), {
            textContent: node.textContent,
          }),
        );
    }
    const fragment = document.createDocumentFragment();
    fragment.append(...parsed.body.childNodes);
    return fragment;
  }
  let dialog, returnFocus;
  function ensureDialog() {
    if (dialog) return dialog;
    dialog = el("dialog", "cq-product-dialog");
    dialog.setAttribute("aria-label", "Product information");
    const close = el("button", "cq-product-close", "×");
    close.type = "button";
    close.setAttribute("aria-label", "Close product details");
    close.addEventListener("click", () => dialog.close());
    dialog.append(close, el("div", "cq-product-dialog-body"));
    dialog.addEventListener("click", (e) => {
      if (e.target === dialog) dialog.close();
    });
    dialog.addEventListener("close", () => returnFocus?.focus());
    document.body.append(dialog);
    return dialog;
  }
  function detail(p, heading = "h2") {
    const wrap = el("div", "cq-product-detail"),
      imageSide = el("div", "cq-product-detail-image"),
      img = el("img");
    const primaryImage = imageURL(p.featured_image || p.images?.[0]);
    if (primaryImage) img.src = primaryImage;
    img.alt = p.title;
    img.width = 600;
    img.height = 840;
    imageSide.append(primaryImage ? img : el("p", "", "CAQA Resources"));
    const pictures = (p.images || []).map(imageURL).filter(Boolean);
    if (pictures.length > 1) {
      const thumbs = el("div", "cq-detail-thumbs");
      pictures.slice(0, 8).forEach((src, i) => {
        const b = el("button");
        b.type = "button";
        b.setAttribute("aria-label", "View product image " + (i + 1));
        const t = el("img");
        t.src = src;
        t.alt = "";
        b.append(t);
        b.addEventListener("click", () => (img.src = src));
        thumbs.append(b);
      });
      imageSide.append(thumbs);
    }
    wrap.append(imageSide);
    const info = el("div", "cq-product-detail-copy");
    info.append(
      el("p", "eyebrow", p.type || "CAQA Resources"),
      el(heading, "", p.title),
    );
    const price = el("p", "cq-detail-price"),
      availability = el("p", "cq-card-meta");
    info.append(price, availability);
    const buy = el("a", "btn btn--primary", "Purchase at CAQA Resources ↗");
    buy.target = "_blank";
    buy.rel = "noopener";
    const variants = p.variants || [];
    const initialVariant = variants.find((v) => v.available) || variants[0];
    const preOrder = (Array.isArray(p.tags) ? p.tags : String(p.tags || '').split(',')).some(t => /^pre[ -]?order$/i.test(t.trim()));
    const update = (v) => {
      price.textContent =
        Number(v?.price ?? p.price) > 0
          ? money(Number(v?.price ?? p.price))
          : "See pricing at CAQA Resources";
      availability.textContent =
        preOrder ? "Pre-order resource. Confirm the delivery timeframe before purchase." : (v?.available ?? p.available)
          ? "Available from CAQA Resources"
          : "Check availability with CAQA Resources";
      buy.href =
        base +
        "/products/" +
        p.handle +
        (v?.id ? "?variant=" + encodeURIComponent(v.id) : "");
      buy.textContent =
        preOrder ? "Pre-order at CAQA Resources ↗" : (v?.available ?? p.available)
          ? "Purchase at CAQA Resources ↗"
          : "View availability at CAQA Resources ↗";
    };
    if (variants.length > 1) {
      const label = el("label", "", "Choose an option"),
        select = el("select");
      select.setAttribute("aria-label", "Product option");
      variants.forEach((v) => {
        const o = el("option", "", v.title);
        o.value = v.id;
        select.append(o);
      });
      select.value = String(initialVariant.id);
      select.addEventListener("change", () =>
        update(variants.find((v) => String(v.id) === select.value)),
      );
      label.append(select);
      info.append(label);
    }
    update(initialVariant);
    info.append(
      buy,
      el(
        "p",
        "cq-catalogue-note",
        "You will continue to CAQA Resources to confirm options, purchase terms and checkout.",
      ),
    );
    const desc = el("div", "cq-detail-description");
    desc.append(
      el("h3", "", "About this resource"),
      safeDescription(p.description),
    );
    wrap.append(info, desc);
    return wrap;
  }
  async function openProduct(handle, fallbackTitle) {
    returnFocus = document.activeElement;
    const d = ensureDialog(),
      body = d.querySelector(".cq-product-dialog-body");
    body.replaceChildren(
      el("p", "cq-product-detail", "Loading product information…"),
    );
    if (!d.open) d.showModal();
    try {
      body.replaceChildren(detail(await product(handle)));
    } catch (e) {
      const error = el("div", "cq-product-detail-copy");
      error.style.padding = "2rem";
      error.append(
        el("h2", "", fallbackTitle || "Product information"),
        el("p", "", e.message),
      );
      const a = el(
        "a",
        "btn btn--primary",
        "View this product at CAQA Resources ↗",
      );
      a.href = base + "/products/" + handle;
      a.target = "_blank";
      a.rel = "noopener";
      error.append(a);
      body.replaceChildren(error);
    }
  }
  function card(p) {
    const node = el("article", "cq-product-card"),
      button = el("button", "cq-product-image");
    button.type = "button";
    button.setAttribute("aria-label", "View " + p.title);
    const image = el("img");
    image.alt = p.title;
    image.loading = "lazy";
    image.width = 400;
    image.height = 560;
    const src = imageURL(
      p.featured_image || p.images?.[0]?.src || p.images?.[0],
    );
    if (src) { image.src=src; button.append(image); }
    else button.append(el("span", "", "CAQA Resources"));
    button.addEventListener("click", () => openProduct(p.handle, p.title));
    const copy = el("div", "cq-product-info");
    copy.append(
      el("span", "cq-product-type", p.type || p.product_type || "Resource"),
      el("h3", "", p.title),
    );
    const cents =
      p.variants?.[0] && typeof p.variants[0].price === "string"
        ? Math.round(Number(p.variants[0].price) * 100)
        : Number(p.price || 0);
    copy.append(
      el(
        "p",
        "cq-product-price",
        cents > 0 ? money(cents) : "View pricing and details",
      ),
    );
    const view = el("button", "btn btn--ghost", "View resource");
    view.type = "button";
    view.addEventListener("click", () => openProduct(p.handle, p.title));
    copy.append(view);
    node.append(button, copy);
    return node;
  }
  let fullIndexPromise;
  async function loadIndex(root) {
    if (!fullIndexPromise)
      fullIndexPromise = (async () => {
        const response = await request(root.dataset.indexUrl),
          packed = await response.json();
        if (packed.unavailable) throw Error("Search index pending");
        if (
          packed.encoding === "gzip-base64" &&
          typeof DecompressionStream !== "undefined"
        ) {
          const bytes = Uint8Array.from(atob(packed.data), (c) =>
            c.charCodeAt(0),
          );
          return new Response(
            new Blob([bytes])
              .stream()
              .pipeThrough(new DecompressionStream("gzip")),
          ).json();
        }
        const manifest = await (
          await request(root.dataset.indexFallback)
        ).json();
        const products = [];
        for (const part of manifest.parts) {
          const u = new URL(root.dataset.indexFallback);
          u.pathname = u.pathname.replace(/[^/]+$/, part);
          u.search = "";
          products.push(...(await (await request(u.href)).json()));
        }
        return { products, types: manifest.types };
      })();
    return fullIndexPromise;
  }
  async function nativeSearch(q, page) {
    const url =
      "/search?type=product&view=cq-catalogue&q=" +
      encodeURIComponent(q) +
      "&page=" +
      page;
    const html = await (await request(url)).text(),
      doc = new DOMParser().parseFromString(html, "text/html");
    return {
      items: [...doc.querySelectorAll("[data-cq-native-product]")].map((e) => ({
        handle: e.dataset.handle,
        title: e.querySelector("h3")?.textContent,
        type: e.dataset.type,
        featured_image: e.querySelector("img")?.getAttribute("src"),
      })),
      total: Number(doc.querySelector("[data-cq-total]")?.dataset.cqTotal || 0),
      more: !!doc.querySelector("[data-cq-next]"),
    };
  }
  const roots = [...document.querySelectorAll("[data-resource-catalogue]")];
  roots.forEach((root) => {
    const form = root.querySelector("[data-resource-form]"),
      field = form.elements["resource-query"],
      select = form.elements["resource-collection"],
      grid = root.querySelector("[data-resource-results]"),
      status = root.querySelector("[data-resource-status]"),
      prev = root.querySelector("[data-resource-prev]"),
      next = root.querySelector("[data-resource-next]"),
      counter = root.querySelector("[data-resource-page]"),
      reset = root.querySelector("[data-resource-reset]");
    const size = root.dataset.featured === "true" ? 4 : 12;
    let page = 1,
      query = "",
      matches = null,
      sequence = 0,
      started = false;
    if (
      [...select.options].some(
        (o) => o.value === root.dataset.initialCollection,
      )
    )
      select.value = root.dataset.initialCollection;
    const busy = () => {
      grid.setAttribute("aria-busy", "true");
      status.textContent = query
        ? "Searching resources…"
        : "Loading resources…";
      prev.disabled = true;
      next.disabled = true;
    };
    async function display() {
      const turn = ++sequence;
      busy();
      try {
        let products,
          total = 0,
          more = false;
        if (query) {
          let index;
          try {
            index = await loadIndex(root);
          } catch {}
          if (index) {
            if (!matches) {
              const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
              matches = index.products.filter((p) =>
                terms.every((t) =>
                  (p[1] + " " + p[0] + " " + p[3]).toLowerCase().includes(t),
                ),
              );
            }
            total = matches.length;
            const slice = matches.slice((page - 1) * size, page * size);
            products = await Promise.all(
              slice.map(async (p) => {
                try {
                  return await product(p[0]);
                } catch {
                  return { handle: p[0], title: p[1], type: index.types[p[2]] };
                }
              }),
            );
            more = page * size < total;
            status.textContent = total.toLocaleString() + " matching resources";
          } else {
            const fallback = await nativeSearch(query, page);
            products = await Promise.all(
              fallback.items.map(async (p) => {
                try {
                  return await product(p.handle);
                } catch {
                  return p;
                }
              }),
            );
            total = fallback.total;
            more = fallback.more;
            status.textContent =
              total.toLocaleString() +
              " results in the CAQA catalogue. Browse collections for the latest resource-store additions.";
          }
        } else {
          const collection = select.value || "all";
          const payload = await (
            await request(
              base +
                "/collections/" +
                encodeURIComponent(collection) +
                "/products.json?limit=" +
                size +
                "&page=" +
                page,
            )
          ).json();
          products = payload.products || [];
          more = products.length === size;
          status.textContent =
            "Browsing " +
            select.options[select.selectedIndex].text.toLowerCase();
        }
        if (turn !== sequence) return;
        grid.replaceChildren(...products.map(card));
        if (!products.length) {
          const empty = el("div", "cq-resource-empty");
          empty.append(
            el(
              "h3",
              "",
              query
                ? "No matching resources"
                : "No resources in this collection",
            ),
            el(
              "p",
              "",
              "Try a qualification or unit code, a shorter phrase, or another collection.",
            ),
          );
          grid.append(empty);
        }
        counter.textContent = "Page " + page;
        prev.disabled = page === 1;
        next.disabled = !more;
        reset.hidden = !query;
        grid.removeAttribute("aria-busy");
      } catch (e) {
        if (turn !== sequence) return;
        status.textContent = e.message;
        const box = el("div", "cq-resource-empty");
        box.append(
          el("h3", "", "Browse CAQA Resources"),
          el(
            "p",
            "",
            "The live catalogue is temporarily unavailable. You can still browse the resource store directly.",
          ),
        );
        const a = el("a", "btn btn--primary", "Open CAQA Resources ↗");
        a.href = base + "/collections/" + select.value;
        a.target = "_blank";
        a.rel = "noopener";
        box.append(a);
        grid.replaceChildren(box);
        grid.removeAttribute("aria-busy");
        counter.textContent = "";
      }
    }
    const start = () => {
      if (started) return;
      started = true;
      const params = new URLSearchParams(location.search);
      if (root.dataset.featured !== "true" && params.get("q")) {
        field.value = params.get("q");
        query = field.value;
      }
      display();
    };
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      started = true;
      query = field.value.trim();
      matches = null;
      page = 1;
      if (query) {
        select.value = "all";
      }
      display();
    });
    select.addEventListener("change", () => {
      started = true;
      field.value = "";
      query = "";
      matches = null;
      page = 1;
      display();
    });
    prev.addEventListener("click", () => {
      if (page > 1) {
        page--;
        display();
      }
    });
    next.addEventListener("click", () => {
      page++;
      display();
    });
    reset.addEventListener("click", () => {
      query = "";
      field.value = "";
      matches = null;
      page = 1;
      display();
    });
    if ("IntersectionObserver" in window) {
      const observer = new IntersectionObserver(
        (items) => {
          if (items.some((i) => i.isIntersecting)) {
            observer.disconnect();
            start();
          }
        },
        { rootMargin: "150px" },
      );
      observer.observe(root);
    } else start();
  });
  document
    .querySelectorAll("[data-resource-product]")
    .forEach(async (target) => {
      try {
        target.replaceChildren(
          detail(await product(target.dataset.resourceProduct), "h1"),
        );
      } catch {
        const note = el(
          "p",
          "",
          "Current product information is temporarily unavailable. Use the link below to view the product on CAQA Resources.",
        );
        target.prepend(note);
      }
    });
  const requested = new URLSearchParams(location.search).get("product");
  if (requested && /^[a-z0-9-]+$/.test(requested)) openProduct(requested);
})();
